'use strict';

require('../extensions/mixin.js');
const Backbone = require('backbone');
const Marionette = require('backbone.marionette');
const Configuration = require('./configuration.js');
const _ = require('lodash');
const obj = require('../utils/object.js');
const collection = require('../utils/collection.js');
const comparison = require('../utils/comparison.js');

const Model = Backbone.Model.extend({
    initialize: function () {
        const me = this;

        me.widgets = new Backbone.Collection();

        // Set initial state
        me.resetProps(me.getProps(), {
            silent: true,
            navigate: false,
            initialize: true
        });
        Marionette.bindEntityEvents(me, me, me.events);
    },

    triggerMethod: Marionette.triggerMethod,

    transformProps: _.identity,

    getProps: function () {
        const me = this;
        return _.omit(me.attributes, 'id');
    },

    getProp: function (key) {
        const me = this;
        return me.get(key);
    },

    propagate: function (changed, options) {
        const me = this;
        options = options || {};

        const current = me.getProps();
        let resolved = me.transformProps(current, {});

        const resolvedChanged = _.pickBy(resolved, function (v, k) {
            return !_.isEqual(current[k], v) || (_.has(changed, k) && _.isEqual(changed[k], resolved[k]));
        });

        resolved = me.transformProps(current, resolvedChanged);
        me.reset(resolved, {
            silent: true
        });

        if (!_.size(resolvedChanged)) {
            return;
        }

        if (!options.silent) {
            _.each(resolvedChanged, function (v, k) {
                me.trigger(`change:${k}`, me, v, options);
            });

            me.trigger('change', me, options);
        }

        me.proxyChangeEventsToChildren(changed, options);
    },

    setProps: function (key, val, options) {
        const me = this;

        let newProps;
        if (_.isObject(key)) {
            newProps = key;
            options = val;
        } else {
            (newProps = {})[key] = val;
        }

        options = _.defaults({}, options, {
            navigate: true,
            reset: false,
            propagate: true
        });

        let currentProps;
        if (options.reset) {
            if (Object.getPrototypeOf(me.attributes) instanceof Object) {
                currentProps = Object.getPrototypeOf(me.attributes);
            } else {
                currentProps = {};
            }
        } else {
            currentProps = me.getProps();
        }

        let changed = _.omitBy(newProps, function (a, k) {
            const b = currentProps[k];
            return _.isEqualWith(a, b, comparison.prototypeComparison);
        });

        // Bail out here as an optimization so we dont hit transformProps
        if (!_.size(changed) && !options.reset) {
            return;
        }

        const resolved = me.transformProps(_.extend({}, _.omit(currentProps, 'id'), changed), changed);
        me.reset(resolved, options);

        // Changed values are now values that changed after resolving props
        changed = me.changed;

        if (options.navigate && me.isFocus()) {
            obj.set(me.queryProps, _.pick(changed, _.keys(newProps)));
            me.navigate();
        }

        if (me.configuration && options.propagate) {
            me.proxyChangeEventsToChildren(changed, options);
        }
    },

    reset: function (props, options) {
        const me = this;
        props = props || {};

        const unset = [];
        for (const key in me.attributes) { // jshint ignore:line
            if (key !== 'id' && !props.hasOwnProperty(key)) {
                props[key] = undefined;
                unset.push(key);
            }
        }

        me.set(props, options);

        _.each(unset, function (key) {
            delete me.attributes[key];
        });
    },

    setPropHash: function (hashName, newProps, options) {
        const me = this;
        if (me[hashName]) {
            obj.set(me[hashName], newProps);
            me.propagate(newProps, options);
        }
    },

    resetPropHash: function (hashName, newProps, options) {
        const me = this;
        const changed = obj.diff(me[hashName], newProps);
        obj.reset(me[hashName], newProps);
        me.propagate(changed, options);
    },

    proxyChangeEventsToChildren: function (changed, options) {
        const me = this;

        if (options.propagate === false) {
            return;
        }

        // If  widgets have not been created yet no need to create them
        // just to event
        if (!me.configuration || !me.configuration.widgetsCached()) {
            return;
        }

        me.getUnfilteredWidgets().invoke('propagate', changed, options);
    },

    // NOTE copied from Backbone.Model to support prototype inheritance on
    // attributes
    set: function (key, val, options) {
        if (key === null) {
            return this;
        }

        // Handle both `"key", value` and `{key: value}` -style arguments.
        let attrs;
        if (typeof key === 'object') {
            attrs = key;
            options = val;
        } else {
            (attrs = {})[key] = val; // jshint ignore:line
        }

        options || (options = {}); // jshint ignore:line

        // Run validation.
        if (!this._validate(attrs, options)) { return false; } // jshint ignore:line

        // Extract attributes and options.
        const unset = options.unset;
        const silent = options.silent;
        const changes = [];
        const changing = this._changing;
        this._changing = true;

        if (!changing) {
        // NOTE Change
            this._previousAttributes = _.extend({}, this.attributes);
            this.changed = {};
        }

        const current = this.attributes;
        const changed = this.changed;
        const prev = this._previousAttributes;

        // For each `set` attribute, update or delete the current value.
        let isChange;
        // eslint-disable-next-line guard-for-in
        for (const attr in attrs) {
            isChange = false;
            val = attrs[attr];
            if (!_.isEqualWith(current[attr], val, comparison.prototypeComparison)) {
                changes.push(attr);
                isChange = true;
            }
            if (!_.isEqualWith(prev[attr], val, comparison.prototypeComparison)) {
                changed[attr] = val;
            } else {
                delete changed[attr];
            }

            if (unset) {
                delete current[attr];
            } else if (isChange) {
            // NOTE Change
                current[attr] = val;
            }
        }

        // NOTE we do not update the id

        // Trigger all relevant attribute changes.
        if (!silent) {
            if (changes.length) { this._pending = options; } // jshint ignore:line
            for (let i = 0; i < changes.length; i++) {
                this.trigger(`change:${changes[i]}`, this, current[changes[i]], options);
            }
        }

        // You might be wondering why there's a `while` loop here. Changes can
        // be recursively nested within `"change"` events.
        if (changing) { return this; } // jshint ignore:line
        if (!silent) {
            while (this._pending) {
                options = this._pending;
                this._pending = false;
                this.trigger('change', this, options);
            }
        }
        this._pending = false;
        this._changing = false;
        return this;
    },

    resetProps: function (props, options) {
        const me = this;
        me.setProps(props || {}, _.extend(options || {}, {
            reset: true
        }));
    },

    isFocus: _.noop,

    navigate: function (options) {
        const me = this;

        if (me.isFocus()) {
            Backbone.history.navigate(me.getPath(), options);
        }
    },

    reload: function () {
        const me = this;

        // TODO validate listener cleanup
        me.stopListening();

        window.requestAnimationFrame(function () {
            Backbone.history.loadUrl(me.getPath(), {
                trigger: true
            });
        });
    },

    getPath: _.noop,

    save: function (attrs, options) {
        const me = this;

        function successFn() {
            if (options.success) {
                options.success.apply(options.context, arguments);
            }
        }

        return me.configuration.save(attrs, _.defaults({
            success: successFn
        }, options));
    },

    /*
     * Default configuration model
     */
    configurationModel: Configuration,

    /*
     * Get a configuration model for saving prop changes
     * and storing widgets
     */
    buildConfiguration: function () {
        const me = this;
        const configuration = new me.configurationModel(me.getConfigurationAttrs());
        configuration.parent = _.get(me, 'curation.configuration');

        return configuration;
    },

    getConfigurationAttrs: function () {
        const me = this;
        const config = {
            id: me.instanceId || me.id
        };

        if (me.instanceId) {
            config.instanceId = me.instanceId;
        }

        return config;
    },

    resetWidgets: function () {
        const me = this;

        if (me.configuration) {
            me.configuration.resetWidgetCache();
        }
    },

    getUnfilteredWidgets: function () {
        const me = this;

        if (!me.configuration) {
            return new Backbone.Collection();
        }

        const widgets = me.configuration.getWidgets(me, !me.widgetId && (me.getProp('widgetId') || me.getProp('instanceId')));

        // If this is a widget return all subwidgets
        if (me.widgetId) {
            return widgets;
        }

        if (me.getProp('widgetId')) {
            return new Backbone.Collection([
                me.configuration.buildWidget(me, me.getProp('widgetId'))
            ]);
        }

        if (me.getProp('instanceId')) {
            return new Backbone.Collection(widgets.find(function (widget) {
                return widget.instanceId === me.getProp('instanceId');
            }));
        }

        return widgets;
    },

    getWidgets: function (options, overrides) {
        const me = this;
        options = _.defaults(options || {}, {
            reset: true
        });

        const widgets = me.getUnfilteredWidgets();

        const models = widgets.reject(function (widget) {
            return _.result(widget, 'filter', false) || me.rejectWidget(widget, overrides);
        });

        if (!options.reset) {
            return new Backbone.Collection(models);
        }

        collection.apply(me.widgets, models, options);

        return me.widgets;
    },

    rejectWidget: _.noop,

    getWidget: function (instanceId) {
        const me = this;

        return me.getWidgets().find(function (widget) {
            return widget.instanceId === instanceId;
        });
    },

    /*
     * Called to generate a dynamic instance id if a static one is not
     * specified
     */
    generateInstanceId: _.noop
});

module.exports = Model;
